/**
 * router/guards.ts —— 路由守卫：合法性判定 + hash 改写 + 写 state
 * 来历：一期 [SEC:router] 段 route() 里的那一大坨改写规则，原样搬过来，数据源换成 meta。
 *
 * ## 为什么守卫能保持同步
 * 它只依赖 shared/meta.ts 里那三样轻量数据（me / periods / teams），boot 时已经拉齐。
 * 详见 meta.ts 顶部说明。**守卫里【不许出现 await】**——一旦异步，
 * 「改写 hash → hashchange 再走一遍」这条链就会打两次请求、闪两次。
 *
 * ## 一期定下、二期继续遵守的两条约定
 * 1. **hash 里始终带当前对象 id**（队伍号 / 期号 / tab / 快报范围）。
 *    刷新和后退才回得到原来那个位置，这是评审时明确要的。
 * 2. **改写完立即 return，让 hashchange 再走一遍**，保证 hash 与 state 永远一致。
 */
import type { Route } from './parse.js';
import { state } from '../shared/state.js';
import * as meta from '../shared/meta.js';

/** 管理后台三个 tab。⚠ 唯一出处 */
export const ADM_TABS = ['periods', 'persons', 'analytics'] as const;

export type GuardResult = { kind: 'redirect'; hash: string } | { kind: 'ok'; route: Route };

export function applyGuards(r: Route | null): GuardResult {
  /* ---- 登录闸门：放在所有业务分支之前 ----
     ⚠ 这一层【只是界面引导，不是权限】。真正的拦截在服务端（红线 4）：
       前端隐藏 = 所有人都能改所有队的数据，只是看不见入口而已。 */
  if (!meta.isLoggedIn()) {
    if (!r || r.view !== 'login') return { kind: 'redirect', hash: '#/login' };
    return { kind: 'ok', route: { view: 'login' } };
  }

  /* 🔴 2026-08-26 起【没有首登强制改密这道闸门】：改密改为线下告知 + 登录页自助入口。
     ⚠ 别看到 `meta.mustChangePassword()` 还在就把这段"补回来"——那个字段只剩
       「这个人还在用初始密码」的记录意义，服务端也不再按它拦任何请求。 */

  /* 已登录还停在登录页 → 回首页，避免用后退键回到一个"假门" */
  if (r && r.view === 'login') return { kind: 'redirect', hash: '#/' };
  if (!r) return { kind: 'redirect', hash: '#/' }; /* 回落，不报错不白屏 */

  /* ⚠ 队伍详情【收归档队】：归档队的档案仍要能看（需求全文「状态穷举」），
     它只是退出本期考核，不是不存在。`meta.teamById()` 含归档，其余三处用 activeTeams()。 */
  if (r.view === 'detail' && !meta.teamById(r.team)) return { kind: 'redirect', hash: '#/' };

  /* 团队 OKR：hash 里始终带队伍号，避免写操作后默认选中项跳走。
     ⚠ 一期默认队是「进度最低的那支」，那要按 okr 排序——okr 是【服务端算好下发】的字段，
       meta.activeTeams() 里就有，前端只是读，不是自己算（红线 4）。 */
  if (r.view === 'okr') {
    const list = meta.activeTeams();
    if (list.length) {
      /* 🔴 2026-09-09：**正式成员默认落到自己的队**（队长也是正式成员，同一条）。
         其他角色（管理员 / 志愿者 / 内外部导师）没有"自己的队"，仍按一期的「进度最低的那支」——
         他们要看的是全营谁落后，队员要看的是自己那一支。
         ⚠ 只影响 hash 缺省时的落点：手动切队照常，写操作后也不会被拽回自己的队。
         ⚠ `teamIds` 可能含已归档的队，所以是在 `activeTeams()` 里找，不是反过来。 */
      const me = meta.me();
      const mine =
        me?.type === 'member' ? (list.find((t) => me.teamIds.includes(t.id))?.id ?? null) : null;
      const fallback = mine ?? list.reduce((a, b) => (a.okr <= b.okr ? a : b)).id;
      const id = list.some((t) => t.id === r.team) ? (r.team as string) : fallback;
      if (id !== r.team) return { kind: 'redirect', hash: '#/okr/' + id };
      state.ok.team = id;
    } else {
      state.ok.team = null; /* 一支在用队都没有：正常空态（刚上线、队伍还没录） */
    }
  }

  /* 双周报：三条改写规则
       · 期号缺省 / 不存在 → 落到默认期（进行中的那期；没有就落到最近一期已结束的）
       · 队伍不存在或已归档 → 去掉队伍段，回到列表（归档队已退出考核）
       · 未开始的期不可填报 → 去掉 /edit（⚠ 服务端也要拒，前端隐藏不算权限，§五 5.6 约束③）
     🔴 与一期最大的差别：**默认期可能是 null**（空当态 / 一期都还没建，红线 3.5）。
       一期 CUR_NO 写死为 3，永远有值；这里 null 时【不改写 hash】，直接让页面渲染空态，
       否则会构造出 '#/reports/pnull' 这种垃圾 hash。 */
  if (r.view === 'reports') {
    const no = meta.periodByNo(r.no) ? (r.no as number) : meta.defaultPeriodNo();
    if (no == null) {
      state.rp.no = null;
      state.rp.team = null;
      state.rp.edit = false;
    } else {
      const tm = meta.teamById(r.team);
      const id = tm && !tm.archived ? tm.id : null;
      const started = isStarted(no);
      const ed = !!r.edit && !!id && started;
      const want = '#/reports/p' + no + (id ? '/' + id : '') + (ed ? '/edit' : '');
      if (location.hash !== want) return { kind: 'redirect', hash: want };
      state.rp.no = no;
      state.rp.team = id;
      state.rp.edit = ed;
      /* ⚠ 换队或换期才重建草稿；同一支队同一期【原地刷新不重建】，
           否则「打开 → 去 OKR 改 KR → 回来提交」这条版本冲突路径走不出来（一期 2026-08-04 定）。
         草稿的重建交给页面 load()，这里只维护 dkey。 */
      if (!ed) {
        state.rp.draft = null;
        state.rp.snap = null;
        state.rp.dkey = '';
      }
    }
  }

  /* 日常打卡：与前两页不同，**裸 #/checkins 是合法状态**（= 不筛队伍，看全营），不改写。
     队伍在这里只是筛选条件，不是页面主体。hash 只在【进入时】预置筛选，之后改下拉不回写 hash。 */
  if (r.view === 'checkins') {
    /* 🔴 【打卡页收归档队】——与 OKR / 双周报**故意不同口径**（W2-7 联调时踩到过）：
         队伍详情页的「查看全部打卡」对归档队也显示，因为**历史打卡必须能查**
         （需求全文「入口与出口」入口 2）。进去之后归档队只是不给「＋ 新增打卡记录」，
         那一层在页面里做（服务端也会拒）。
       ⚠ 所以这里【只判存不存在】，不判 archived。改成判 archived 会让那个按钮点了跳回全营。 */
    if (r.team && !meta.teamById(r.team)) return { kind: 'redirect', hash: '#/checkins' };
    if (state.view !== 'checkins' || (r.team || '') !== state.ck.team) state.ck.team = r.team || '';
  }

  /* 项目快报：hash 里始终带**周期**（2026-08-25 起）。
     🔴 与一期最大的两处差别，**不要照搬一期**：
       ① 一期锁定当期、hash 里带的是「范围」（'all' 或队伍号）——**范围那一维已经砍掉了**
          （单队快报不做），周期改由管理员显式选择；
       ② 默认期是「最近一个**已结束**的期」，不是进行中的那个——「阶段快报」的正常语义
          就是回顾已收口的一期（`16` §九）。⚠ 因此这里**不能**用 `defaultPeriodNo()`，
          那个是双周报的默认（优先进行中的期），两处口径不一样。
     ⚠ 一期都还没建时**不改写 hash**，直接让页面渲染空态（红线 3.5）——
       改写会构造出 '#/bulletins/pnull' 这种垃圾 hash。
     ⚠ 【仅管理员与队长】（2026-10-19 起队长权限与管理员一致）：其他人手输 hash 直接回首页。
       **这只是界面引导，不是权限**——接口那一侧 requireAdminOrLead 才是真正的拦截（红线 4）。 */
  if (r.view === 'bulletins') {
    if (!meta.isAdminOrLead()) return { kind: 'redirect', hash: '#/' };
    const fallback = meta.lastClosedPeriod()?.no ?? meta.currentPeriod()?.no ?? null;
    const no = meta.periodByNo(r.no) ? (r.no as number) : fallback;
    if (no == null) {
      state.bl.no = null;
    } else {
      /* ⚠ 期号有效时保留历史版本那一段；期号被改写（无效 / 缺省）时那一段一并丢掉——它属于别的期 */
      const want = '#/bulletins/p' + no + (r.no === no && r.id ? '/' + r.id : '');
      if (location.hash !== want) return { kind: 'redirect', hash: want };
      /* 换期要清掉「历史浮层展开」这类瞬时状态——它属于上一期的那份记录 */
      if (state.bl.no !== no) {
        state.bl.no = no;
        state.bl.hist = false;
      }
    }
  }

  /* 管理后台：裸 #/admin 与非法 tab 一并改写为 #/admin/periods，刷新与后退才回得到原来那个 tab。
     ⚠ 【仅管理员】（基线 §6：人员、部门、队伍与周期配置由管理员完成）：
       非管理员手输 hash 直接回首页。**这只是界面引导，不是权限**——
       接口那一侧 requireAdmin 才是真正的拦截（红线 4）。 */
  if (r.view === 'admin') {
    if (!meta.isAdmin()) return { kind: 'redirect', hash: '#/' };
    if (!ADM_TABS.some((x) => x === r.tab)) return { kind: 'redirect', hash: '#/admin/periods' };
    state.ad.tab = r.tab as string;
  }

  state.view = r.view;
  state.team = r.view === 'detail' ? r.team : null;
  if (r.view === 'detail') state.from = r.from;
  else state.hit = null;

  return { kind: 'ok', route: r };
}

/**
 * 这一期开始了没有。
 * ⚠ 只用于「未开始的期不许填报」这一条界面判断，**不是口径计算**——
 *   真正的拦截在服务端（§五 5.6 约束③）。日期比较用的是后端下发的 'YYYY-MM-DD' 字符串，
 *   字典序即时间序，**不许 new Date()**：一 new 就掉进浏览器本地时区，全站日差会错。
 */
function isStarted(no: number): boolean {
  if (!meta.periodByNo(no)) return false;
  // 有进行中的期就以它为界，没有（空当态）就以最近一期已结束的为界；
  // 期号大于这个界的都是「尚未开始」，一律不许进编辑页。
  const cur = meta.currentPeriod()?.no ?? meta.lastClosedPeriod()?.no ?? null;
  return cur !== null && no <= cur;
}
